/**
 * 원장 PDF 등록 화면: 문제지·정답지·해설지 업로드 → 자동 정리 → 원문 대조(문제 있는 항목만 열림, 그 항목만 정정) → 확인 후 일괄 등록.
 * 화면은 판정하지 않는다. 구역·번호 매칭과 막는 문제 판정은 서버 미리보기 응답이다.
 * 등록은 승인·현재 기준 적용·학생 배포 허용이 아니다. 결과를 못 받은 등록은 같은 내용으로만 다시 보낸다(서버가 같은 내용은 새로 만들지 않음).
 * OCR은 지원하지 않는다(스캔 PDF는 서버가 거부하고 안내).
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';
import {
  evidenceReasonText, groupExceptions, failMessage, issueText, mergeCorrection, parseAccepted, parseOptions, previewBody, registerBlocker, registerBody, toBase64,
  countIssueKinds, isSemanticsOnly, splitTarget, type EvidenceInfo, type CorrectionDraft, type EvidenceException, type MultiSemantics, type PdfFiles, type Role,
} from '../core/pdf-import.js';
import { newRequestKey } from '../core/transport.js';
import { mountSchoolGradePicker } from './school-grade-picker.js';

interface Point { number: number; semantics?: string; options: string[]; accepted: number[]; explanation: string | null; raw: { question: string; answer: string | null; explanation: string | null }; corrected: boolean; issues: string[] }
interface Section { name: string; pointCount: number; stemMarked: string; points: Point[] }
interface Candidate { section: string; number: number; category: string; stem: string; options: string[]; correctNo: number; sourceQuote: string | null; distractorsReviewed: boolean }
interface EvidenceLinks { summary: { total: number; auto: number; needsReview: number; notApplicable: number }; exceptions: EvidenceException[]; candidates: Candidate[]; categories?: { id: string; label: string }[] }
interface Preview { sectionCount: number; pointCount: number; blockingCount: number; issues: { code: string; blocking: boolean; section?: string; number?: number; detail?: string }[]; sections: Section[]; registrable: boolean; evidenceLinks?: EvidenceLinks }

const read = async (f: File): Promise<string> => toBase64(new Uint8Array(await f.arrayBuffer()));

/** 교사·원장 공용. 교사는 담당 반(서버가 /admin/classes로 돌려준 반)을 골라야 등록되고, 범위 밖 반은 서버가 거부한다 */
export function mountPdfImport(t: Transport, role: Role = 'director'): HTMLElement {
  const gen = currentGen();
  const live = () => gen === currentGen();
  let files: PdfFiles | null = null;
  let corrections: CorrectionDraft[] = [];
  let preview: Preview | null = null;
  let busy = false;
  let registered = false;
  // 확인 필요 지점의 근거 유형 선택(선택 사항, 직접 입력 없음). 키: `구역#번호`. 새 미리보기에서 더는 고를 수 없는 지점은 버린다.
  const picks = new Map<string, string>();

  const pick =(label: string, id: string) => {
    const input = h('input', { type: 'file', id, accept: 'application/pdf,.pdf', 'data-testid': id });
    return { input, node: h('label', { class: 'review-field', for: id }, label, input) };
  };
  const q = pick('문제지 PDF', 'pdf-question');
  const a = pick('정답지 PDF', 'pdf-answer');
  const e = pick('해설지 PDF', 'pdf-explanation');
  const msg = h('p', { role: 'status', 'data-testid': 'pdf-message' });
  const out = h('div', { 'data-testid': 'pdf-preview' });
  const classSel = h('select', { id: 'pdf-class', 'data-testid': 'pdf-class' }, h('option', { value: '' }, role === 'teacher' ? '담당 반 선택' : '반 지정 안 함(원장)'));
  void (async () => {
    const c = await t.request('GET', '/admin/classes');
    if (!live() || c.kind !== 'http' || c.status !== 200) return;
    for (const x of c.body['classes'] as { id: string; name: string }[]) classSel.append(h('option', { value: x.id }, x.name));
  })();
  // 적용 대상: 학생 첫설정과 같은 /schools 카탈로그(같은 학교 ID·학년). 켠 쌍만 등록 뒤 기존 대상 제공 API로 전달한다
  const picker = mountSchoolGradePicker(t, live, () => undefined, 'pdf');
  const analyze = h('button', { type: 'button', class: 'primary', 'data-testid': 'pdf-analyze', onclick: () => void run(true) }, 'PDF 자동 정리');
  // 복수 정답의 뜻을 한 번에 정한다(지점별 정정이 있으면 그 지점은 정정이 우선). 고르지 않으면 지점마다 정해야 등록할 수 있다
  const bulkSel = h('select', { id: 'pdf-bulk-sem', 'data-testid': 'pdf-bulk-sem', 'aria-label': '복수 정답 전체의 뜻', onchange: () => { if (preview !== null) void run(false); } },
    h('option', { value: '' }, '지점마다 직접 정하기'), h('option', { value: 'any_one_accepted' }, '모두: 하나만 골라도 정답'), h('option', { value: 'all_required' }, '모두: 인정 정답을 전부 골라야 정답'));
  const bulk = (): MultiSemantics | undefined => (bulkSel.value === 'any_one_accepted' || bulkSel.value === 'all_required' ? bulkSel.value : undefined);

  async function load(): Promise<boolean> {
    const fs = [q.input.files?.[0], a.input.files?.[0], e.input.files?.[0]];
    if (fs.some((f) => f === undefined)) { msg.textContent = '문제지·정답지·해설지 PDF를 모두 선택해 주세요.'; return false; }
    files = { questionPdf: await read(fs[0]!), answerPdf: await read(fs[1]!), explanationPdf: await read(fs[2]!) };
    return true;
  }

  async function run(fresh: boolean): Promise<void> {
    if (busy) return;
    busy = true; analyze.disabled = true; registered = false;
    try {
      if (fresh) { corrections = []; if (!(await load())) return; }
      if (files === null) return;
      msg.textContent = 'PDF에서 글자를 읽고 정리하는 중…';
      const r = await t.request('POST', '/admin/content/pdf-imports/preview', { body: previewBody(files, corrections, bulk()) });
      if (!live()) return;
      if (r.kind !== 'http') { msg.textContent = '결과를 받지 못했어요. 같은 버튼으로 다시 시도해 주세요(저장된 것은 없어요).'; return; }
      if (r.status !== 200) { preview = null; clear(out); msg.textContent = failMessage(r.status, r.body); return; }
      preview = r.body as unknown as Preview;
      const still = new Set((preview.evidenceLinks?.exceptions ?? []).filter((x) => x.pickable === true).map((x) => `${x.section}#${x.number}`));
      for (const k of [...picks.keys()]) if (!still.has(k)) picks.delete(k);
      const kinds = countIssueKinds(preview.sections);
      msg.textContent = preview.blockingCount > 0
        ? `정리했어요. 확인이 필요한 항목 ${preview.blockingCount}건이 있어요.${kinds.semanticsOnly > 0 ? ` 그중 복수 정답의 뜻만 정하면 되는 지점 ${kinds.semanticsOnly}개(정답은 정상으로 읽혔어요, 공통 규칙 하나로 한 번에 정해요).` : ''}${kinds.real > 0 ? ` 실제로 고쳐야 하는 지점 ${kinds.real}개(정답 연결 실패·원문 불일치 등).` : ''} (아직 저장되지 않았어요)`
        : `정리했어요. ${preview.sectionCount}개 구역, ${preview.pointCount}개 지점. 원문과 대조한 뒤 등록해 주세요. (아직 저장되지 않았어요)`;
      render();
    } finally { busy = false; analyze.disabled = false; }
  }

  function correctionForm(s: Section, p: Point): HTMLElement {
    const opts = h('textarea', { rows: 4, 'aria-label': `${s.name} ${p.number}번 보기(한 줄에 하나)` }, p.options.join('\n'));
    opts.value = p.options.join('\n');
    const acc = h('input', { 'aria-label': `${s.name} ${p.number}번 인정 정답 번호(쉼표)` });
    acc.value = p.accepted.join(', ');
    const ex = h('textarea', { rows: 3, 'aria-label': `${s.name} ${p.number}번 해설` });
    ex.value = p.explanation ?? '';
    const err = h('p', { role: 'alert' });
    // 복수 인정 정답의 뜻은 자동으로 정하지 않는다: 사람이 고르기 전에는 등록이 막힌다
    // 정답이 해석되지 않은 지점("둘 다" 등)을 교사가 번호 2개로 정정할 때도 같은 화면에서 뜻을 정할 수 있도록 항상 보인다.
    // 인정 정답이 1개면 서버가 뜻을 쓰지 않는다(all_required는 거부). 보기 수로 뜻을 추정하지 않는다.
    const sem = h('select', { 'data-testid': `pdf-sem-${s.name}-${p.number}`, 'aria-label': `${s.name} ${p.number}번 복수 정답의 뜻` },
      h('option', { value: '' }, '선택해 주세요(인정 정답이 1개면 비워 두세요)'), h('option', { value: 'any_one_accepted' }, '하나만 골라도 정답(인정 정답 중 어느 것이든)'),
      h('option', { value: 'all_required' }, '모두 골라야 정답(인정 정답을 전부 고른 경우만)'));
    return h('div', { class: 'pdf-fix' },
      h('label', { class: 'review-field' }, '보기(한 줄에 하나)', opts),
      h('label', { class: 'review-field' }, '인정 정답 번호(여러 개면 쉼표)', acc),
      h('label', { class: 'review-field' }, '해설', ex),
      h('label', { class: 'review-field' }, '인정 정답이 여러 개일 때', sem),
      err,
      h('button', { type: 'button', class: 'secondary', 'data-testid': `pdf-fix-${s.name}-${p.number}`, onclick: () => {
        const options = parseOptions(opts.value);
        const accepted = parseAccepted(acc.value, options.length);
        if (accepted === null) { err.textContent = '인정 정답은 보기 번호(1~보기 수)로 입력해 주세요.'; return; }
        if (options.length < 2) { err.textContent = '보기는 2개 이상이어야 해요.'; return; }
        const semantics = sem.value === 'any_one_accepted' || sem.value === 'all_required' ? sem.value : undefined;
        corrections = mergeCorrection(corrections, { section: s.name, number: p.number, options, accepted, explanation: ex.value.trim(), ...(semantics ? { semantics } : {}) });
        void run(false);
      } }, '이 항목만 고쳐서 다시 정리'));
  }

  function pointNode(s: Section, p: Point): HTMLElement {
    const bad = p.issues.length > 0;
    // 복수 정답의 뜻만 비어 있는 지점은 정답이 정상 인식된 것이므로 열어서 재확인시키지 않는다(공통 규칙 또는 예외 정정으로 처리)
    const semOnly = isSemanticsOnly(p.issues);
    const real = bad && !semOnly;
    const body = [
      h('div', { class: 'pdf-compare' },
        h('div', {}, h('strong', {}, '문제지 원문'), h('pre', {}, p.raw.question)),
        h('div', {}, h('strong', {}, '정답지 원문'), h('pre', {}, p.raw.answer ?? '(없음)')),
        h('div', {}, h('strong', {}, '해설지 원문'), h('pre', {}, p.raw.explanation ?? '(없음)'))),
      h('ol', {}, ...p.options.map((o, i) => h('li', { class: p.accepted.includes(i + 1) ? 'pdf-accepted' : '' }, `${o}${p.accepted.includes(i + 1) ? '  ← 인정 정답' : ''}`))),
      bad ? h('ul', { role: 'alert' }, ...p.issues.map((c) => h('li', {}, issueText(c)))) : null,
      bad || p.corrected ? correctionForm(s, p) : null,
    ];
    return h('details', { class: 'review-row', 'data-testid': `pdf-point-${s.name}-${p.number}`, ...(real ? { open: true } : {}) },
      h('summary', {}, `${p.number}번 · 인정 정답 ${p.accepted.join(', ') || '없음'}${real ? ' · 확인 필요' : semOnly ? ' · 정답 인식 정상 · 복수 정답 뜻 대기' : p.corrected ? ' · 고침' : ' · 이상 없음'}`), ...body);
  }

  /** 등록된 구역별 자료에 켠 (학교, 학년) 쌍만 전달한다. 등록 자체는 이미 끝났으므로 실패해도 등록은 그대로이고, 제공 패널에서 다시 만들 수 있다 */
  async function provide(releaseIds: string[], append: (text: string) => void): Promise<void> {
    const pairs = picker.pairs();
    if (pairs.length === 0) return;
    let made = 0;
    let students = 0;
    for (const releaseId of releaseIds) {
      const r = await t.request('POST', '/admin/material-targets', { body: { releaseId, pairs }, key: newRequestKey() });
      if (!live()) return;
      if (r.kind === 'http' && (r.status === 201 || r.status === 200)) { made += 1; students = Number(r.body['includedStudents'] ?? students); }
    }
    append(made === releaseIds.length
      ? ` 선택한 학교-학년 ${pairs.length}쌍에 ${made}개 구역을 제공 대상으로 만들었어요(지금 맞는 학생 ${students}명). 학생 배포 허용은 아직 꺼져 있어요.`
      : ` 제공 대상은 ${releaseIds.length}개 중 ${made}개만 만들었어요. 나머지는 '학교·학년별 자료 제공'에서 다시 만들어 주세요(등록은 그대로예요).`);
  }

  /**
   * 원장 전용 일괄 검토·승인·적용: 지점마다 검수 기록 → 정답 기준 승인(+적용)을 기존 공식 API로 따로 남긴다(합쳐서 하나의 승인이 아님).
   * 원문 대조 확인을 따로 체크해야 하고, 실패한 지점은 건너뛰지 않고 멈춘다. 같은 지점·같은 기록 ID는 다시 눌러도 같은 결과다.
   * 학생 배포 허용은 바꾸지 않는다. 교사는 이 구역을 볼 수 없다(서버도 원장만 승인 허용).
   */
  function approvalNode(items: { itemId: string; section: string }[]): HTMLElement {
    const sure = h('input', { type: 'checkbox', 'data-testid': 'pdf-approve-confirm' });
    const note = h('p', { role: 'status', 'data-testid': 'pdf-approve-message' });
    const btn = h('button', { type: 'button', class: 'primary', 'data-testid': 'pdf-approve-all', onclick: async () => {
      if (!sure.checked) { note.textContent = '원문과 정답 기준을 대조했다는 확인에 체크해 주세요.'; return; }
      btn.disabled = true;
      let done = 0;
      const total = items.reduce((n, it) => n + (preview?.sections.find((s) => s.name === it.section)?.points.length ?? 0), 0);
      try {
        for (const it of items) {
          const sec = preview?.sections.find((s) => s.name === it.section);
          for (const p of sec?.points ?? []) {
            const ext = `${it.itemId}-P${p.number}`;
            const rec = `PDFREV-${it.itemId}-P${p.number}`.slice(0, 64);
            const k1 = newRequestKey();
            const a = await t.request('POST', '/admin/content/answer-keys/review-records', { body: { pointExternalId: ext, keyVersion: 'v1', reviewRecordId: rec, note: 'PDF 일괄 등록 화면에서 원장이 원문과 대조 후 확인' }, key: k1 });
            if (!live()) return;
            if (a.kind !== 'http' || (a.status !== 201 && a.status !== 200)) { note.textContent = `${ext}: 검수 기록을 저장하지 못해 멈췄어요(${done}/${total}개 처리). 같은 버튼으로 이어서 처리해요.`; return; }
            const b = await t.request('POST', '/admin/content/approvals', { body: { targetKind: 'answer_key', externalId: ext, version: 'v1', approvalRecordId: rec, action: 'approve', applyReason: 'PDF 등록 화면 일괄 승인·적용(원장 확인)' }, key: newRequestKey() });
            if (!live()) return;
            if (b.kind !== 'http' || (b.status !== 201 && b.status !== 200)) { note.textContent = `${ext}: 승인·적용하지 못해 멈췄어요(${done}/${total}개 처리).`; return; }
            done += 1;
            note.textContent = `처리 중 ${done}/${total}`;
          }
        }
        note.textContent = `${done}개 지점의 정답 기준을 승인하고 현재 기준으로 적용했어요. 학생 배포 허용은 바꾸지 않았어요.`;
      } finally { btn.disabled = false; }
    } }, '정답 기준 일괄 승인·적용');
    return h('section', { 'data-testid': 'pdf-approve-box' }, h('h3', {}, '4. 정답 기준 승인·적용(원장)'),
      h('p', {}, '등록만으로는 학생이 풀 수 없어요. 원문과 대조한 정답 기준을 지점마다 승인하고 현재 기준으로 적용해야 시작할 수 있어요.'),
      h('label', {}, sure, ' 위 원문 대조 결과로 정답 기준을 승인·적용해요'), btn, note);
  }

  /**
   * 근거 문제 자동 연결 검토: 요약 수치(서버 미리보기 값 그대로), 실제 근거 질문·보기·정답·출처, 이유별 예외.
   * 후보는 검증된 정답이 아니다(오답 보기는 고정 유형 목록이라 유일정답성·관련성이 교육적으로 확정되지 않음). 하나씩 클릭할 필요는 없다.
   * 예외 지점은 근거 문제 없이 등록되고('근거 자료 없음'), 나중에 보완한다. 직접 입력은 필수가 아니다.
   */
  /** 근거 유형 판단용 실제 자료: 대상 문장(대상 표현 강조), 모든 보기와 인정 정답, 업로드 해설, 전체 지문. 문장을 하나로 정할 수 없으면 그렇게 밝힌다 */
  function contextNodes(info: EvidenceInfo | null | undefined, sectionName: string): HTMLElement[] {
    if (info === null || info === undefined) return [];
    const passage = preview?.sections.find((s) => s.name === sectionName)?.stemMarked;
    const nodes: (HTMLElement | null)[] = [
      info.sentence === null
        ? h('p', { 'data-testid': 'pdf-ctx-sentence' }, '대상 문장: 지문에서 이 지점의 문장을 하나로 정하지 못했어요(원문이 불확실해요). 아래 전체 지문을 보고 판단해 주세요.')
        : h('p', { 'data-testid': 'pdf-ctx-sentence' }, h('strong', {}, '대상 문장: '), ...splitTarget(info.sentence).map((t) => (t.target ? h('mark', {}, t.text) : t.text))),
      h('ol', {}, ...info.options.map((o, i) => h('li', { class: info.accepted.includes(i + 1) ? 'pdf-accepted' : '' }, `${o}${info.accepted.includes(i + 1) ? '  ← 인정 정답' : ''}`))),
      h('p', {}, h('strong', {}, '업로드 해설: '), info.explanation ?? '(없음)'),
      passage === undefined ? null : h('details', {}, h('summary', {}, '전체 지문 펼치기(지점 자리는 {{번호}})'), h('pre', {}, passage)),
    ];
    return nodes.filter((n): n is HTMLElement => n !== null);
  }

  function evidenceNode(ev: EvidenceLinks): HTMLElement {
    const s = ev.summary;
    const groups = groupExceptions(ev.exceptions);
    return h('section', { 'data-testid': 'pdf-evidence' }, h('h3', {}, '근거 문제 자동 연결(해설 기반, 외부 AI 없음)'),
      h('p', { 'data-testid': 'pdf-evidence-summary' }, `전체 ${s.total}지점 · 자동 연결 후보 ${s.auto} · 확인 필요 ${s.needsReview} · 근거 자료 없음(어휘·추론) ${s.notApplicable}`),
      h('p', {}, '후보는 업로드 해설에 적힌 문법 근거에서 만든 선택형 질문이에요. 검증된 정답이라는 뜻이 아니며, 오답 보기는 고정 유형 목록이라 알맞은지는 따로 판단해야 해요.'),
      h('details', { 'data-testid': 'pdf-evidence-candidates' }, h('summary', {}, `자동 연결 후보 ${ev.candidates.length}개 보기(하나씩 확인하지 않아도 돼요)`),
        ...ev.candidates.map((c) => h('details', { class: 'review-row' }, h('summary', {}, `${c.section} ${c.number}번 · ${c.category}`),
          h('p', {}, c.stem),
          h('ol', {}, ...c.options.map((o, i) => h('li', { class: i + 1 === c.correctNo ? 'pdf-accepted' : '' }, `${o}${i + 1 === c.correctNo ? '  ← 정답 후보' : ''}`))),
          h('p', {}, `출처(해설 근거): ${c.sourceQuote ?? '없음'}`)))),
      groups.length === 0 ? null : h('div', { 'data-testid': 'pdf-evidence-exceptions' }, h('p', {}, `확인 필요 ${s.needsReview}개는 근거 문제 없이 등록돼요('근거 자료 없음'으로 표시, 나중에 보완). 문제 풀이 채점에는 영향이 없어요.`),
        h('p', {}, '유형을 고르면 그 지점에 선택형 근거 문제가 만들어져요. 고르는 것은 선택이고 직접 쓰지 않아도 돼요. 고른 유형이 알맞은지는 아직 검증되지 않았어요.'),
        ...groups.map((g) => {
          const pickable = g.items.filter((x) => x.pickable === true);
          const fixed = g.items.length - pickable.length;
          return h('details', { class: 'review-row' }, h('summary', {}, `${evidenceReasonText(g.reason)} · ${g.items.length}개`),
            h('p', {}, g.items.map((x) => `${x.section} ${x.number}번`).join(', ')),
            ...(pickable.length === 0 || ev.categories === undefined ? [] : pickable.map((x) => {
              const key = `${x.section}#${x.number}`;
              const prev = h('div', { 'data-testid': `pdf-pick-preview-${x.number}`, role: 'status' });
              const showPreview = (v: string) => {
                clear(prev);
                const pv = v === '' ? undefined : x.info?.previews?.[v];
                if (v === '') prev.append(h('p', {}, '근거 없이 두기: 이 지점은 "근거 자료 없음"으로 등록돼요(통과로 세지 않아요).'));
                else if (pv === undefined) prev.append(h('p', {}, '이 유형으로 만들 질문을 서버에서 받지 못했어요. 근거 없이 두는 것을 권해요.'));
                else prev.append(h('p', {}, h('strong', {}, '학생에게 나올 근거 질문(미리보기)')), h('pre', {}, pv.stem), h('ol', {}, ...pv.options.map((o, i) => h('li', { class: i + 1 === pv.correctNo ? 'pdf-accepted' : '' }, `${o}${i + 1 === pv.correctNo ? '  ← 정답' : ''}`))),
                  h('p', {}, '고른 유형이 알맞은지는 아직 검증되지 않았어요. 오답 보기는 고정 유형 목록이에요.'));
              };
              const sel = h('select', { 'data-testid': `pdf-pick-${x.number}`, 'aria-label': `${x.section} ${x.number}번 근거 유형`, onchange: (e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                if (v === '') picks.delete(key); else picks.set(key, v);
                showPreview(v);
              } }, h('option', { value: '' }, '근거 없이 두기'), ...ev.categories!.map((c) => h('option', { value: c.id, ...(picks.get(key) === c.id ? { selected: true } : {}) }, c.label)));
              showPreview(picks.get(key) ?? '');
              return h('div', { class: 'review-row', 'data-testid': `pdf-pick-card-${x.number}` }, h('strong', {}, `${x.section} ${x.number}번`), ...contextNodes(x.info, x.section), h('label', { class: 'review-field' }, '근거 유형 ', sel), prev);
            })),
            fixed > 0 ? h('p', { 'data-testid': 'pdf-pick-unavailable' }, `${fixed}개는 복수 정답 등으로 유형을 고를 수 없어요. 근거 자료 없음으로 남아요(통과로 세지 않아요).`) : null,
            ...g.items.filter((x) => x.pickable !== true && x.info != null).map((x) => h('details', {}, h('summary', {}, `${x.section} ${x.number}번 자료 보기(유형을 고를 수 없는 지점)`), ...contextNodes(x.info, x.section))));
        })));
  }

  function render(): void {
    clear(out);
    if (preview === null) return;
    const pv = preview;
    const general = pv.issues.filter((i) => i.number === undefined);
    if (general.length > 0) out.append(h('ul', { role: 'alert', 'data-testid': 'pdf-general-issues' }, ...general.map((i) => h('li', {}, `${i.section ?? '전체'}: ${issueText(i.code)}${i.detail ? ` (${i.detail})` : ''}`))));
    const kinds = countIssueKinds(pv.sections);
    if (kinds.semanticsOnly > 0) {
      // 복수 정답이라는 사실만으로 뜻을 추정하지 않는다: 공통 규칙은 사람이 한 번 고른다. 고르면 모든 복수 정답 지점에 적용되고, 예외만 각 지점에서 따로 정정한다.
      const apply = (v: MultiSemantics) => { bulkSel.value = v; void run(false); };
      out.append(h('section', { 'data-testid': 'pdf-bulk-panel', class: 'review-row' },
        h('h3', {}, `복수 정답 ${kinds.semanticsOnly}개 지점의 공통 규칙을 한 번만 정해 주세요`),
        h('p', {}, '정답 번호는 문제지·정답지·해설지에서 정상으로 읽혔어요. 정해야 하는 것은 뜻뿐이에요. 규칙을 고르면 이 지점들에 한꺼번에 적용되고, 다른 규칙이 필요한 지점만 아래에서 따로 고치면 돼요.'),
        h('button', { type: 'button', class: 'primary', 'data-testid': 'pdf-bulk-all-required', onclick: () => apply('all_required') }, '모두 골라야 정답으로 일괄 적용'),
        h('button', { type: 'button', class: 'secondary', 'data-testid': 'pdf-bulk-any', onclick: () => apply('any_one_accepted') }, '하나만 골라도 정답으로 일괄 적용')));
    }
    for (const s of pv.sections) {
      const badCount = s.points.filter((p) => p.issues.length > 0 && !isSemanticsOnly(p.issues)).length;
      out.append(h('section', { 'data-testid': `pdf-section-${s.name}` },
        h('h3', {}, `${s.name} · ${s.points.length}개 지점${badCount > 0 ? ` · 확인 필요 ${badCount}` : ''}`),
        h('details', {}, h('summary', {}, '지문(지점 자리는 {{번호}}로 표시)'), h('pre', {}, s.stemMarked)),
        ...s.points.map((p) => pointNode(s, p))));
    }
    if (pv.pointCount === 0) return;
    if (pv.evidenceLinks !== undefined) out.append(evidenceNode(pv.evidenceLinks));
    const autoEv = h('input', { type: 'checkbox', checked: true, 'data-testid': 'pdf-auto-evidence' });
    autoEv.checked = true;
    const evidenceAvailable = (pv.evidenceLinks?.summary.auto ?? 0) > 0 || (pv.evidenceLinks?.exceptions ?? []).some((x) => x.pickable === true);
    const setId = h('input', { id: 'pdf-set-id', 'data-testid': 'pdf-set-id', maxlength: '40', placeholder: '비워 두면 자동' });
    const title = h('input', { id: 'pdf-title', 'data-testid': 'pdf-title', maxlength: '150', placeholder: '비워 두면 자동' });
    const confirm = h('input', { type: 'checkbox', 'data-testid': 'pdf-confirm' });
    // 등록 결과는 긴 페이지 위쪽의 msg가 아니라 버튼 바로 옆에도 남긴다(오류는 alert, 처리·완료는 status). 보이도록 스크롤·포커스한다.
    const regMsg = h('p', { 'data-testid': 'pdf-register-status', tabindex: '-1', role: 'status' });
    const say = (text: string, kind: 'status' | 'alert' | 'progress' = 'status'): void => {
      regMsg.setAttribute('role', kind === 'alert' ? 'alert' : 'status');
      regMsg.setAttribute('data-kind', kind);
      regMsg.textContent = text;
      msg.textContent = text;
      if (kind !== 'progress') { regMsg.focus({ preventScroll: true }); regMsg.scrollIntoView?.({ block: 'center' }); }
    };
    const idBox = h('details', {}, h('summary', {}, '자료 ID·제목 직접 정하기(선택)'),
      h('label', { class: 'review-field', for: 'pdf-set-id' }, '자료 ID(영문·숫자·-·_)', setId),
      h('label', { class: 'review-field', for: 'pdf-title' }, '자료 제목', title));
    const reg = h('button', { type: 'button', class: 'primary', 'data-testid': 'pdf-register', onclick: async () => {
      if (busy || files === null) return;
      const input = { setId: setId.value.trim(), releaseTitle: title.value.trim(), confirmed: confirm.checked, confirmAnswerKey: confirm.checked, classId: classSel.value, autoEvidence: autoEv.checked && evidenceAvailable, evidencePicks: [...picks].map(([k, category]) => { const i = k.lastIndexOf('#'); return { section: k.slice(0, i), number: Number(k.slice(i + 1)), category }; }), ...(bulk() ? { multiAnswerSemantics: bulk()! } : {}) };
      const block = registerBlocker(input, pv.blockingCount, role);
      if (block !== null) { say(block, 'alert'); return; }
      busy = true; reg.disabled = true;
      // 서버가 단계(구역별 적재→정답 기준 확인·적용)를 안쪽에서 처리하는 동안 실제 단계 진행률은 알 수 없다: 꾸미지 않고 경과 시간만 보여 준다
      const t0 = Date.now();
      const tick = (): void => { const s = Math.floor((Date.now() - t0) / 1000); say(`등록하는 중… ${s}초 경과. 파일은 이미 전송됐고 서버가 구역별 적재와 정답 기준 확인을 하고 있어요. 닫거나 새로고침하지 말고, 버튼을 다시 누르지 말고 기다려 주세요.${s >= 30 ? ' 시간이 걸리고 있지만 아직 실패한 것이 아니에요. 서버 응답이 올 때까지 기다려 주세요.' : ''}`, 'progress'); };
      tick();
      const timer = setInterval(tick, 1000);
      try {
        const r = await t.request('POST', '/admin/content/pdf-imports', { body: registerBody(files, corrections, input) });
        clearInterval(timer);
        if (!live()) return;
        if (r.kind !== 'http') { say('등록됐는지 확인하지 못했어요(연결이 끊겼거나 응답이 오지 않았어요). 등록이 됐을 수도, 안 됐을 수도 있어요. 입력은 그대로 남아 있으니 같은 입력으로 한 번 다시 누르면 중복 없이 같은 내용으로 확인해요.', 'alert'); return; }
        if (r.status === 201) {
          registered = true;
          const rv = (r.body['answerSemanticsReview'] as unknown[] | undefined)?.length ?? 0;
          say(`등록 완료(자료 ID ${String(r.body['setId'])}): ${(r.body['registered'] as unknown[]).length}개 구역, ${String(r.body['pointCount'])}개 지점. 복수 정답 검토 기록 ${rv}건. ${r.body['approved'] === true ? '확인한 정답 기준을 적용했어요. 대상 학교·학년 학생이 바로 시작할 수 있어요(학생 배포 허용 설정은 바뀌지 않아요).' : '정답 기준 확인·적용은 아직 하지 않았어요.'}`);
          await provide((r.body['registered'] as { releaseId: unknown }[]).map((x) => String(x.releaseId)), (text) => { regMsg.textContent += text; msg.textContent += text; });
          if (role === 'director' && r.body['approved'] !== true) out.append(approvalNode((r.body['registered'] as { itemId: unknown; section: unknown }[]).map((x) => ({ itemId: String(x.itemId), section: String(x.section) }))));
        } else if (r.status === 409 && r.body['error'] === 'content_version_conflict' && ((r.body['registered'] as unknown[] | undefined)?.length ?? 0) === 0) {
          // 등록된 구역이 하나도 없는 충돌: 같은 자료 ID·버전에 이미 다른 설정으로 등록돼 있다. 새 테스터 자료는 새 자료 ID로 등록한다.
          idBox.open = true;
          say('등록되지 않았어요(새로 등록된 구역 0개). 같은 원본이 이미 이 자료 ID로 등록돼 있어서 기존 내용은 그대로 두었어요. 별도 테스터 자료로 등록하려면 아래 "자료 ID"에 새 ID(예: tester-1)를 입력하고 다시 눌러 주세요. 올린 파일·미리보기·고친 값·선택은 그대로 남아 있어요.', 'alert');
          setId.focus();
        } else if (r.status === 409 && ((r.body['registered'] as unknown[] | undefined)?.length ?? 0) > 0) {
          say(`일부만 등록되고 멈췄어요(등록된 구역 ${(r.body['registered'] as unknown[]).length}개, 멈춘 구역 ${String(r.body['section'])}). 이미 등록된 구역은 그대로이며 같은 입력으로 다시 누르면 이어서 등록해요.`, 'alert');
        } else say(failMessage(r.status, r.body), 'alert');
      } finally { clearInterval(timer); busy = false; reg.disabled = registered; }
    } }, '확인했어요 · 일괄 등록');
    out.append(h('section', { 'data-testid': 'pdf-register-box' }, h('h3', {}, '3. 확인 후 일괄 등록'),
      pv.blockingCount > 0 ? h('p', { role: 'alert' }, '확인이 필요한 항목을 모두 고치기 전에는 등록할 수 없어요.') : null,
      h('label', { class: 'review-field', for: 'pdf-class' }, role === 'teacher' ? '등록할 담당 반' : '반(선택)', classSel),
      h('fieldset', { 'data-testid': 'pdf-targets' }, h('legend', {}, '자동 제공할 학교·학년(선택)'),
        h('p', {}, '켠 학교-학년 쌍에 맞는 학생에게만 자료가 보여요. 학교 목록은 학생 첫설정과 같아요. 켜지 않으면 등록만 해요.'), picker.node),
      idBox,
      !evidenceAvailable ? null : h('label', { class: 'review-field' }, autoEv, ` 자동 연결 후보 ${pv.evidenceLinks?.summary.auto ?? 0}개와 내가 고른 유형을 근거 문제로 함께 등록해요(직접 입력 없음)`),
      h('label', {}, confirm, ' 원문과 대조해 확인했어요'), reg, regMsg,
      h('p', {}, '확인하고 등록하면 정답 기준이 이 계정 이름으로 확인·적용돼요(원장 별도 승인 없음, 확인한 사람이 기록에 남아요). 학생 배포 허용 설정은 바꾸지 않아요.')));
  }

  return h('section', { 'data-testid': 'pdf-import' },
    h('h2', {}, 'PDF로 자료 등록'),
    h('p', {}, '문제지·정답지·해설지 PDF를 올리면 구역과 번호로 맞춰 정리해요. 문제가 있는 항목만 고치면 돼요. 글자가 들어 있는 PDF만 지원하고, 스캔(이미지) PDF는 읽을 수 없어요.'),
    h('h3', {}, '1. 파일 선택'), q.node, a.node, e.node,
    h('label', { class: 'review-field', for: 'pdf-bulk-sem' }, '인정 정답이 여러 개인 지점의 뜻(선택)', bulkSel), analyze,
    h('h3', {}, '2. 원문 대조'), msg, out);
}
